import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Dialog, Notification, toast } from "components/ui";
import { CONTRACT_LINK_CLASS_NAME } from "constants/moduleConstants/contract.constant";
import { apiApproveContract } from "services/ContractService";
import processSignupContractApprovals from "utils/processSignupContractApprovals";
import fetchDomainPendingContracts from "utils/fetchDomainPendingContracts";
import getPendingContractLabel from "utils/getPendingContractLabel";
import { useTranslation } from "react-i18next";

const ContractApprovalGate = () => {
  const { t } = useTranslation();
  const token = useSelector((state) => state.auth?.session?.token);
  const signedIn = useSelector((state) => state.auth?.session?.signedIn);
  const [pendingContracts, setPendingContracts] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadPendingContracts = useCallback(async () => {
    const items = await fetchDomainPendingContracts();
    setPendingContracts(items);
    setIsOpen(items.length > 0);
    return items;
  }, []);

  useEffect(() => {
    if (!token || !signedIn) {
      setPendingContracts([]);
      setIsOpen(false);
      return;
    }

    let cancelled = false;

    const bootstrap = async () => {
      try {
        await processSignupContractApprovals();
        if (cancelled) return;
        await loadPendingContracts();
      } catch {
        if (!cancelled) {
          setPendingContracts([]);
          setIsOpen(false);
        }
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
    };
  }, [token, signedIn, loadPendingContracts]);

  const handleApproveAll = async () => {
    if (!pendingContracts.length) {
      setIsOpen(false);
      return;
    }

    setIsSubmitting(true);
    const failed = [];

    try {
      for (const contract of pendingContracts) {
        try {
          await apiApproveContract(contract.contractversionid);
        } catch {
          failed.push(contract);
        }
      }

      if (failed.length) {
        setPendingContracts(failed);
        toast.push(
          <Notification
            title={t("auth.signUp.form.contracts.pendingErrorTitle")}
            type="danger"
            duration={4000}
          >
            {t("auth.signUp.form.contracts.pendingError")}
          </Notification>,
          { placement: "top-center" },
        );
        return;
      }

      setPendingContracts([]);
      setIsOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Dialog
      isOpen={isOpen}
      closable={false}
      width={520}
      contentClassName="dialog-content !p-6"
    >
      <h4 className="mb-2">{t("auth.signUp.form.contracts.pendingTitle")}</h4>
      <p className="mb-5 text-sm text-gray-600 dark:text-gray-300">
        {t("auth.signUp.form.contracts.pendingDesc")}
      </p>
      <ul className="mb-6 space-y-3 text-left">
        {pendingContracts.map((contract) => {
          const typeLabel = getPendingContractLabel(contract, t);

          return (
            <li key={contract.contractversionid}>
              <a
                href={contract.document}
                target="_blank"
                rel="noopener noreferrer"
                className={CONTRACT_LINK_CLASS_NAME}
              >
                {typeLabel}
              </a>
              {contract.companyname ? (
                <span className="ml-1 text-xs text-gray-500">
                  ({contract.companyname})
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
      <Button
        block
        variant="solid"
        loading={isSubmitting}
        onClick={handleApproveAll}
      >
        {t("auth.signUp.form.contracts.approveButton")}
      </Button>
    </Dialog>
  );
};

export default ContractApprovalGate;
